import { ActionBar, Label, Modal, Text } from '@viapeople/components'
import { useState } from 'react'
import { trpc } from '../../utils/trpc'
import { type Column, GenericTable } from '../GenericTable'

interface User {
	id: string
	organizationId: string
	email: string
	firstName: string
	lastName: string
	externalId: string
	active: boolean
}

type UserSearchInput = {
	search?: string
	page?: number
	perPage?: number
	sortBy?: 'email' | 'firstName' | 'lastName' | 'externalId'
	order?: 'asc' | 'desc'
}

interface AddUsersModalProps {
	isOpen: boolean
	excludedUserExternalIds: () => Set<string>
	onSelect: (users: User[]) => void
	onClose: () => void
	organizationId: string
	title?: string
}

export const AddUsersModal = ({
	isOpen,
	onClose,
	organizationId,
	excludedUserExternalIds,
	onSelect,
	title = 'Add Users',
}: AddUsersModalProps) => {
	const [selectedUsers, setSelectedUsers] = useState<User[]>([])

	const columns: Column<User>[] = [
		{
			field: 'firstName',
			label: 'Name',
			colSpan: 2,
			render: (user: User) => (
				<Text variant='xsmall' weight='regular'>
					{`${user.firstName} ${user.lastName}`}
				</Text>
			),
		},
		{
			field: 'externalId',
			label: 'ID',
			colSpan: 1,
		},
		{
			field: 'email',
			label: 'Email',
			colSpan: 2,
		},
		{
			field: 'active',
			label: 'Status',
			sortable: false,
			render: (user: User) => (
				<div className='flex'>
					<Label className='self-start' color={user.active ? 'green' : 'default'}>
						{user.active ? 'Active' : 'Inactive'}
					</Label>
				</div>
			),
		},
	]

	const handleClose = () => {
		setSelectedUsers([])
		onClose()
	}

	const handleSelect = () => {
		onSelect(selectedUsers)
		setSelectedUsers([])
		onClose()
	}

	return (
		<Modal isOpen={isOpen} onClose={handleClose} title={title}>
			<div className='flex flex-col gap-4'>
				<Text variant='small' className='text-gray-600'>
					Select users to add. Use the search to find specific users.
				</Text>

				<GenericTable<User, UserSearchInput>
					queryOptionsGenerator={(options: UserSearchInput) => ({
						...trpc.client.users.search.queryOptions({
							...options,
							organizationId,
							excludingIds: Array.from(excludedUserExternalIds()),
							excludeCustomFields: true,
						}),
						enabled: !!organizationId && isOpen,
					})}
					selectable
					onSelectionChange={setSelectedUsers}
					columns={columns}
					emptyState={{
						title: 'No users available',
						description: 'No users match your search criteria or all users are already selected',
					}}
				/>

				<ActionBar
					variant='modal'
					rightActions={[
						{
							label: 'Cancel',
							onClick: handleClose,
							variant: 'secondary',
						},
						{
							label: `Add ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`,
							onClick: handleSelect,
							variant: 'primary',
							disabled: selectedUsers.length === 0,
						},
					]}
				/>
			</div>
		</Modal>
	)
}
